// src/components/layout/TopBar.tsx
// Fixed top toolbar — notifications + messaging actions on the right.
import React, {useState} from "react";
import { Badge, Button, Tooltip } from "antd";
import { MessageOutlined, QuestionOutlined, FormOutlined } from "@ant-design/icons";
import { useMessagingPanel } from "../../context/MessagingContext";
import Notifications from "../notifications/Notifications";
import { hasFeature } from "../../utils/subscriptions/subscriptionService";
import CreateSupportTicketModal from "../../pages/help/CreateSupportTicketModal";

interface TopBarProps {
	collapsed: boolean;
	showTour?: () => void;
	isPatient?: boolean;
}

const TopBar: React.FC<TopBarProps> = ({ collapsed, showTour, isPatient }) => {
	const { isPanelOpen, totalUnreadCount, queueCount, openPanel } = useMessagingPanel();
	const showMessages = hasFeature('healthcare');
	const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState(false);
	

	const totalBadge = totalUnreadCount + queueCount;

	const tooltipTitle =
		queueCount > 0
			? `Messages · ${queueCount} patient${queueCount > 1 ? "s" : ""} waiting in queue`
			: "Messages";


	return (
		<header className={`mg-topbar${collapsed ? " sidebar-collapsed" : ""}`}>
			<div style={{ flex: 1 }} />
			<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
				{isPatient && (
					<Tooltip title="Show Tour" placement="bottom">
						<Button
							type="text"
							icon={<QuestionOutlined />}
							onClick={showTour}
							style={{
								width: 32,
								height: 32,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								borderRadius: 6,
								fontSize: 14,
								color: isPanelOpen ? "var(--mg-primary)" : "var(--mg-text-secondary)",
								background: isPanelOpen ? "var(--mg-primary-bg)" : "transparent",
							}}
						/>
					</Tooltip>
				)}
				
				{!isPatient && (
					<Tooltip title="Submit Feedback" placement="bottom">
						<Button
							type="text"
							icon={<FormOutlined />}
							onClick={() => setIsFeedbackFormOpen(true)}
							style={{
								width: 32,
								height: 32,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								borderRadius: 6,
								fontSize: 14,
								color: isPanelOpen ? "var(--mg-primary)" : "var(--mg-text-secondary)",
								background: isPanelOpen ? "var(--mg-primary-bg)" : "transparent",
							}}
						/>
					</Tooltip>
				)}
				<Notifications />
				{showMessages && (
					<Tooltip title={tooltipTitle} placement="bottom">
						<Badge count={totalBadge} size="small" offset={[-4, 4]} style={queueCount > 0 ? { backgroundColor: "var(--mg-warning)" } : undefined}>
							<Button
								data-testid="messaging-open-btn"
								type="text"
								icon={<MessageOutlined />}
								onClick={() => openPanel()}
								style={{
									width: 32,
									height: 32,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									borderRadius: 6,
									fontSize: 14,
									color: isPanelOpen ? "var(--mg-primary)" : "var(--mg-text-secondary)",
									background: isPanelOpen ? "var(--mg-primary-bg)" : "transparent",
								}}
							/>
						</Badge>
					</Tooltip>
				)}

				{isFeedbackFormOpen && (
					<CreateSupportTicketModal
						open={isFeedbackFormOpen}
						onClose={() => setIsFeedbackFormOpen(false)}
						onSuccess={() => setIsFeedbackFormOpen(false)}
					/>
				)}
			</div>
		</header>
	);
};

export default TopBar;
